import { useState, useEffect, useRef } from 'react';
import { getProducts, getProductByBarcode, getCategories } from '../lib/api';
import { Plus, Search, Edit2, Trash2, Warehouse, ArrowRight, MapPin, PackageOpen, Camera, ScanBarcode } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api from '../lib/api';
import { useRefresh } from '../components/Layout';
import { BarcodeScanner } from '../components/BarcodeScanner';

// Generate random barcode
const generateBarcode = () => {
    const timestamp = Date.now().toString().slice(-8);
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `INT${timestamp}${random}`;
};

export const Depozit = ({ safeMode = false }) => {
    const { refreshStats } = useRefresh();
    const [depozitItems, setDepozitItems] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [locationFilter, setLocationFilter] = useState('');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [activePallet, setActivePallet] = useState(null);
    
    // Barcode scanning state
    const [barcodeInput, setBarcodeInput] = useState('');
    const [currentProduct, setCurrentProduct] = useState(null);
    const [step, setStep] = useState(1); // 1 = scan, 2 = confirm
    const [showNewProductForm, setShowNewProductForm] = useState(false);
    const [newProductBarcode, setNewProductBarcode] = useState('');
    const [newProductName, setNewProductName] = useState('');
    const [newProductPrice, setNewProductPrice] = useState('');
    const [newProductCategory, setNewProductCategory] = useState('');
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const barcodeInputRef = useRef(null);
    
    const [formData, setFormData] = useState({
        quantity: '1',
        location: 'P01'
    });
    
    const [editFormData, setEditFormData] = useState({
        quantity: '',
        location: ''
    });
    
    const [moveQuantity, setMoveQuantity] = useState('1');
    
    // Categories autocomplete
    const [categories, setCategories] = useState([]);
    
    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef(null);

    // Predefined locations
    const locations = ['P01', 'P02', 'P03', 'P04', 'P05', 'P06', 'P07', 'P08', 'P09', 'P10'];

    // Filter items by search query (name, barcode, description, location)
    const filteredItems = depozitItems.filter(item => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        return (
            item.name?.toLowerCase().includes(q) ||
            item.barcode?.toLowerCase().includes(q) ||
            item.description?.toLowerCase().includes(q) ||
            item.location?.toLowerCase().includes(q)
        );
    });

    useEffect(() => {
        loadData();
        loadActivePallet();
        loadCategories();
    }, []);
    
    const loadCategories = async () => {
        try {
            const response = await getCategories();
            setCategories(response.data);
        } catch (err) {
            console.error('Error loading categories:', err);
        }
    };

    const loadData = async (location = '') => {
        try {
            setLoading(true);
            const [depozitRes, productsRes] = await Promise.all([
                api.get(`/depozit${location ? `?location=${location}` : ''}`),
                getProducts()
            ]);
            setDepozitItems(depozitRes.data);
            setProducts(productsRes.data);
        } catch (err) {
            toast.error('Eroare la încărcarea datelor');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadActivePallet = async () => {
        try {
            const response = await api.get('/pallets/active');
            if (response.data.active) {
                setActivePallet(response.data.pallet);
            }
        } catch (err) {
            console.error('Error loading pallet:', err);
        }
    };

    const handleFilterSearch = (e) => {
        e.preventDefault();
        loadData(locationFilter);
    };

    const openAddModal = () => {
        setStep(1);
        setBarcodeInput('');
        setCurrentProduct(null);
        setShowNewProductForm(false);
        setNewProductBarcode('');
        setNewProductName('');
        setNewProductPrice('');
        setNewProductCategory('');
        setFormData({ quantity: '1', location: 'P01' });
        setIsAddModalOpen(true);
        
        setTimeout(() => {
            if (barcodeInputRef.current) {
                barcodeInputRef.current.focus();
            }
        }, 100);
    };

    const openMoveModal = (item) => {
        setSelectedItem(item);
        setMoveQuantity('1');
        setIsMoveModalOpen(true);
    };

    const openEditModal = (item) => {
        setSelectedItem(item);
        setEditFormData({
            quantity: item.quantity.toString(),
            location: item.location
        });
        setIsEditModalOpen(true);
    };

    // Handle barcode scanning
    const handleBarcodeKeyDown = async (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            
            if (!barcodeInput.trim()) {
                setNewProductBarcode(generateBarcode());
                setShowNewProductForm(true);
                return;
            }
            
            try {
                const response = await getProductByBarcode(barcodeInput.trim());
                if (response.data) {
                    setCurrentProduct(response.data);
                    setStep(2);
                }
            } catch (err) {
                if (err.response?.status === 404) {
                    setNewProductBarcode(barcodeInput.trim());
                    setShowNewProductForm(true);
                } else {
                    toast.error('Eroare la căutarea produsului');
                }
            }
        }
    };

    const handleScannedBarcode = async (barcode) => {
        setIsScannerOpen(false);
        setBarcodeInput(barcode);
        
        try {
            const response = await getProductByBarcode(barcode);
            if (response.data) {
                setCurrentProduct(response.data);
                setStep(2);
            }
        } catch (err) {
            if (err.response?.status === 404) {
                setNewProductBarcode(barcode);
                setShowNewProductForm(true);
            } else {
                toast.error('Eroare la căutarea produsului');
            }
        }
    };

    // Add existing product to depozit
    const handleAddExistingProduct = async () => {
        if (!currentProduct) return;
        
        try {
            await api.post('/depozit', {
                product_id: currentProduct.id,
                quantity: parseInt(formData.quantity),
                location: formData.location.toUpperCase()
            });
            toast.success(`"${currentProduct.name}" adăugat în Depozit la ${formData.location}`);
            
            // Reset for next scan
            setBarcodeInput('');
            setCurrentProduct(null);
            setFormData({ quantity: '1', location: formData.location });
            setStep(1);
            
            loadData(locationFilter);
            refreshStats();
            
            setTimeout(() => {
                if (barcodeInputRef.current) {
                    barcodeInputRef.current.focus();
                }
            }, 100);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la adăugare');
            console.error(err);
        }
    };

    // Create new product and add to depozit
    const handleCreateNewProduct = async () => {
        if (!newProductName.trim()) {
            toast.error('Introdu numele produsului');
            return;
        }
        
        try {
            const barcode = newProductBarcode.trim() || generateBarcode();
            const price = parseFloat(newProductPrice) || 0;
            
            // Create product
            const productRes = await api.post('/products', {
                name: newProductName.trim(),
                barcode: barcode,
                price: price,
                category: newProductCategory.trim() || null
            });
            
            // Add to depozit
            await api.post('/depozit', {
                product_id: productRes.data.id,
                quantity: parseInt(formData.quantity),
                location: formData.location.toUpperCase()
            });
            
            toast.success(`"${newProductName.trim()}" creat și adăugat în Depozit la ${formData.location}`);
            
            // Reset
            setBarcodeInput('');
            setNewProductBarcode('');
            setNewProductName('');
            setNewProductPrice('');
            setNewProductCategory('');
            setShowNewProductForm(false);
            setFormData({ quantity: '1', location: formData.location });
            setStep(1);
            
            loadData(locationFilter);
            refreshStats();
            
            setTimeout(() => {
                if (barcodeInputRef.current) {
                    barcodeInputRef.current.focus();
                }
            }, 100);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la creare');
            console.error(err);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!selectedItem) return;
        
        try {
            await api.put(`/depozit/${selectedItem.id}`, {
                quantity: parseInt(editFormData.quantity),
                location: editFormData.location.toUpperCase()
            });
            toast.success('Modificări salvate');
            setIsEditModalOpen(false);
            loadData(locationFilter);
            refreshStats();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la salvare');
            console.error(err);
        }
    };

    const handleMoveToMagazin = async () => {
        if (!selectedItem) return;
        
        const qty = parseInt(moveQuantity);
        if (qty > selectedItem.quantity) {
            toast.error('Cantitate insuficientă');
            return;
        }
        
        try {
            await api.post('/depozit/move-to-magazin', {
                item_id: selectedItem.id,
                quantity: qty
            });
            toast.success(`${qty} bucăți mutate în magazin`);
            setIsMoveModalOpen(false);
            loadData(locationFilter);
            refreshStats();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la mutare');
            console.error(err);
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm('Sigur dorești să ștergi acest articol din depozit?')) return;
        
        try {
            await api.delete(`/depozit/${item.id}`);
            toast.success('Articol șters');
            loadData(locationFilter);
            refreshStats();
        } catch (err) {
            toast.error('Eroare la ștergere');
            console.error(err);
        }
    };

    // Get unique locations from items
    const usedLocations = [...new Set(depozitItems.map(item => item.location))].sort();
    const totalItems = depozitItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

    return (
        <div data-testid="depozit-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Depozit
                    </h1>
                    <p className="text-zinc-500 mt-1">
                        {totalItems} produse în {usedLocations.length} locații
                        {activePallet && (
                            <span className="ml-2 text-amber-600">
                                (Palet {activePallet.number} activ)
                            </span>
                        )}
                    </p>
                </div>
                <Button
                    onClick={openAddModal}
                    data-testid="add-depozit-button"
                    className="bg-amber-500 hover:bg-amber-600 text-white h-12 px-6"
                >
                    <Plus size={20} className="mr-2" />
                    Adaugă în Depozit
                </Button>
            </div>

            {/* Search and Location Filter */}
            <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-6">
                {/* Main Search Bar */}
                <div className="relative mb-4">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        placeholder="Caută în depozit (nume, cod de bare, descriere, locație)..."
                        data-testid="depot-search-input"
                        className="pl-10 h-12 text-base"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 text-lg"
                        >
                            ✕
                        </button>
                    )}
                </div>
                
                {/* Location filter row */}
                <form onSubmit={handleFilterSearch} className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                        <Input
                            type="text"
                            value={locationFilter}
                            onChange={(e) => setLocationFilter(e.target.value)}
                            placeholder="Filtrează după locație (P01, P02...)"
                            data-testid="location-filter-input"
                            className="pl-10 h-11"
                        />
                    </div>
                    <Button type="submit" variant="outline" className="h-11">
                        <Search size={18} className="mr-2" />
                        Filtrează
                    </Button>
                </form>
                
                {/* Quick location filters */}
                <div className="flex flex-wrap gap-2 mt-3">
                    <span className="text-xs text-zinc-500 self-center">Locații:</span>
                    {locations.map(loc => {
                        const hasItems = usedLocations.includes(loc);
                        return (
                            <button
                                key={loc}
                                onClick={() => {
                                    setLocationFilter(loc);
                                    loadData(loc);
                                }}
                                className={`px-3 py-1 text-xs rounded-full border transition-all ${
                                    locationFilter === loc
                                        ? 'bg-amber-100 border-amber-300 text-amber-700'
                                        : hasItems
                                            ? 'bg-amber-50 border-amber-200 text-amber-600 hover:bg-amber-100'
                                            : 'bg-zinc-50 border-zinc-200 text-zinc-400'
                                }`}
                            >
                                {loc}
                            </button>
                        );
                    })}
                    {locationFilter && (
                        <button
                            onClick={() => {
                                setLocationFilter('');
                                loadData('');
                            }}
                            className="px-3 py-1 text-xs text-zinc-500 hover:text-zinc-700"
                        >
                            Toate
                        </button>
                    )}
                </div>
            </div>

            {/* Items List */}
            {loading ? (
                <div className="space-y-3">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="bg-white border border-zinc-200 rounded-lg p-4">
                            <Skeleton className="h-5 w-1/3 mb-2" />
                            <Skeleton className="h-4 w-1/4" />
                        </div>
                    ))}
                </div>
            ) : filteredItems.length === 0 ? (
                <div className="empty-state bg-white border border-zinc-200 rounded-lg">
                    <Warehouse size={64} className="text-zinc-300" />
                    <h3 className="text-lg font-medium text-zinc-900 mt-4">
                        {searchQuery ? `Niciun rezultat pentru "${searchQuery}"` : locationFilter ? `Nimic la ${locationFilter}` : 'Depozit gol'}
                    </h3>
                    <p className="text-zinc-500 mt-1">
                        {searchQuery ? 'Încearcă alt termen de căutare' : locationFilter ? 'Încearcă altă locație' : 'Adaugă marfă în depozit'}
                    </p>
                    {!locationFilter && !searchQuery && (
                        <Button
                            onClick={openAddModal}
                            className="mt-4 bg-amber-500 hover:bg-amber-600"
                        >
                            <Plus size={20} className="mr-2" />
                            Adaugă în Depozit
                        </Button>
                    )}
                </div>
            ) : (
                <>
                    {/* Search results count */}
                    {searchQuery && (
                        <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                            <span className="text-sm text-amber-700">
                                <Search size={14} className="inline mr-1" />
                                {filteredItems.length} rezultate pentru "{searchQuery}"
                            </span>
                        </div>
                    )}
                    
                    <div className="space-y-3">
                        {filteredItems.map((item) => (
                            <div
                                key={item.id}
                                data-testid={`depozit-item-${item.id}`}
                                className="bg-white border border-amber-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                            >
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <span className="bg-amber-100 text-amber-700 text-sm font-bold px-3 py-1 rounded-full flex items-center gap-1">
                                                <MapPin size={12} />
                                                {item.location}
                                            </span>
                                            <span className="text-sm text-zinc-500">
                                                {item.quantity} buc.
                                            </span>
                                        </div>
                                        <h3 className="font-semibold text-zinc-900 mt-2">
                                            {item.product?.name || item.name || 'Produs necunoscut'}
                                        </h3>
                                        {(item.product?.barcode || item.barcode) && (
                                            <p className="text-xs font-mono text-zinc-400 mt-1">
                                                #{item.product?.barcode || item.barcode}
                                            </p>
                                        )}
                                        {item.product?.price > 0 && (
                                            <p className="text-sm text-zinc-600 mt-1">
                                            {item.product.price.toFixed(0)} RON/buc • 
                                            <span className="font-semibold"> Total: {(item.product.price * item.quantity).toFixed(0)} RON</span>
                                        </p>
                                    )}
                                </div>
                                
                                <div className="flex items-center gap-2">
                                    {!safeMode && (
                                        <Button
                                            onClick={() => openEditModal(item)}
                                            variant="outline"
                                            size="sm"
                                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                            title="Editează"
                                        >
                                            <Edit2 size={16} />
                                        </Button>
                                    )}
                                    <Button
                                        onClick={() => openMoveModal(item)}
                                        data-testid={`move-to-magazin-${item.id}`}
                                        variant="outline"
                                        size="sm"
                                        className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                                    >
                                        <ArrowRight size={16} className="mr-1" />
                                        Magazin
                                    </Button>
                                    {!safeMode && (
                                        <button
                                            onClick={() => handleDelete(item)}
                                            data-testid={`delete-depozit-${item.id}`}
                                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 rounded"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                    </div>
                </>
            )}

            {/* Add Modal - Similar to Customers */}
            <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Adaugă în Depozit
                        </DialogTitle>
                    </DialogHeader>
                    
                    {step === 1 && !showNewProductForm && (
                        <div className="space-y-4 mt-4">
                            {activePallet && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                    <p className="text-sm text-amber-700">
                                        <PackageOpen size={16} className="inline mr-1" />
                                        Intră pe Palet {activePallet.number}
                                    </p>
                                </div>
                            )}
                            
                            <div>
                                <Label className="flex items-center gap-2">
                                    <ScanBarcode size={18} />
                                    Scanează sau tastează cod
                                </Label>
                                <div className="flex gap-2 mt-1">
                                    <Input
                                        ref={barcodeInputRef}
                                        value={barcodeInput}
                                        onChange={(e) => setBarcodeInput(e.target.value)}
                                        onKeyDown={handleBarcodeKeyDown}
                                        placeholder="Cod de bare..."
                                        className="h-12 font-mono flex-1"
                                        autoFocus
                                    />
                                    <Button
                                        type="button"
                                        onClick={() => setIsScannerOpen(true)}
                                        className="h-12 w-12 bg-[#E11D48] hover:bg-[#BE123C]"
                                        title="Deschide camera"
                                    >
                                        <Camera size={20} />
                                    </Button>
                                </div>
                                <p className="text-xs text-zinc-500 mt-1">Enter gol = produs nou</p>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Cantitate</Label>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={formData.quantity}
                                        onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label>Locație</Label>
                                    <Select
                                        value={formData.location}
                                        onValueChange={(value) => setFormData({ ...formData, location: value })}
                                    >
                                        <SelectTrigger className="mt-1">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {locations.map((loc) => (
                                                <SelectItem key={loc} value={loc}>
                                                    {loc}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            
                            <Button
                                onClick={() => {
                                    setNewProductBarcode(generateBarcode());
                                    setShowNewProductForm(true);
                                }}
                                variant="outline"
                                className="w-full"
                            >
                                <Plus size={18} className="mr-2" />
                                Produs Nou (fără cod)
                            </Button>
                            
                            <Button onClick={() => setIsAddModalOpen(false)} variant="outline" className="w-full">
                                Închide
                            </Button>
                        </div>
                    )}
                    
                    {showNewProductForm && (
                        <div className="space-y-4 mt-4">
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                <p className="text-sm text-amber-700">Produs nou - se creează și se adaugă în depozit</p>
                            </div>
                            
                            <div>
                                <Label>Cod de Bare</Label>
                                <Input
                                    value={newProductBarcode}
                                    onChange={(e) => setNewProductBarcode(e.target.value)}
                                    placeholder="Auto-generat..."
                                    className="mt-1 font-mono"
                                />
                            </div>
                            
                            <div>
                                <Label>Nume Produs *</Label>
                                <Input
                                    value={newProductName}
                                    onChange={(e) => setNewProductName(e.target.value)}
                                    placeholder="Ex: Set pahare"
                                    className="mt-1"
                                    autoFocus
                                />
                            </div>
                            
                            <div>
                                <Label>Categorie</Label>
                                <Input
                                    value={newProductCategory}
                                    onChange={(e) => setNewProductCategory(e.target.value)}
                                    placeholder="Ex: Bucătărie, Baie, Living"
                                    className="mt-1"
                                    list="depozit-categories-list"
                                />
                                <datalist id="depozit-categories-list">
                                    {categories.map((cat, i) => (
                                        <option key={i} value={cat} />
                                    ))}
                                </datalist>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <Label>Cantitate</Label>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={formData.quantity}
                                        onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label>Preț (RON)</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={newProductPrice}
                                        onChange={(e) => setNewProductPrice(e.target.value)}
                                        placeholder="0"
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label>Locație</Label>
                                    <Select
                                        value={formData.location}
                                        onValueChange={(value) => setFormData({ ...formData, location: value })}
                                    >
                                        <SelectTrigger className="mt-1">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {locations.map((loc) => (
                                                <SelectItem key={loc} value={loc}>
                                                    {loc}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            
                            <div className="flex gap-3">
                                <Button
                                    onClick={() => {
                                        setShowNewProductForm(false);
                                        setNewProductBarcode('');
                                        setNewProductName('');
                                        setNewProductPrice('');
                                        setNewProductCategory('');
                                    }}
                                    variant="outline"
                                    className="flex-1"
                                >
                                    Înapoi
                                </Button>
                                <Button
                                    onClick={handleCreateNewProduct}
                                    className="flex-1 bg-amber-500 hover:bg-amber-600"
                                    disabled={!newProductName.trim()}
                                >
                                    Creează + Adaugă
                                </Button>
                            </div>
                        </div>
                    )}
                    
                    {step === 2 && currentProduct && !showNewProductForm && (
                        <div className="space-y-4 mt-4">
                            <div className="bg-zinc-50 rounded-lg p-4">
                                <h3 className="font-semibold text-zinc-900">{currentProduct.name}</h3>
                                <p className="text-sm text-zinc-500">Preț estimat: ~{currentProduct.price?.toFixed(0) || '0'} RON</p>
                                {currentProduct.category && (
                                    <span className="inline-block mt-2 text-xs bg-zinc-200 text-zinc-600 px-2 py-0.5 rounded">
                                        {currentProduct.category}
                                    </span>
                                )}
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Cantitate</Label>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={formData.quantity}
                                        onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label>Locație</Label>
                                    <Select
                                        value={formData.location}
                                        onValueChange={(value) => setFormData({ ...formData, location: value })}
                                    >
                                        <SelectTrigger className="mt-1">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {locations.map((loc) => (
                                                <SelectItem key={loc} value={loc}>
                                                    {loc}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            
                            <div className="flex gap-3">
                                <Button
                                    onClick={() => {
                                        setStep(1);
                                        setBarcodeInput('');
                                        setCurrentProduct(null);
                                    }}
                                    variant="outline"
                                    className="flex-1"
                                >
                                    Înapoi
                                </Button>
                                <Button onClick={handleAddExistingProduct} className="flex-1 bg-amber-500 hover:bg-amber-600">
                                    Adaugă în Depozit
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Barcode Scanner */}
            <BarcodeScanner
                isOpen={isScannerOpen}
                onClose={() => setIsScannerOpen(false)}
                onScan={handleScannedBarcode}
            />

            {/* Move to Magazin Modal */}
            <Dialog open={isMoveModalOpen} onOpenChange={setIsMoveModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Mută în Magazin
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedItem && (
                        <div className="space-y-4 mt-4">
                            <div className="bg-zinc-50 rounded-lg p-4">
                                <p className="font-medium text-zinc-900">{selectedItem.product?.name}</p>
                                <p className="text-sm text-zinc-500 mt-1">
                                    Disponibil: {selectedItem.quantity} buc. (la {selectedItem.location})
                                </p>
                            </div>
                            
                            <div>
                                <Label htmlFor="moveQty">Cantitate de mutat</Label>
                                <Input
                                    id="moveQty"
                                    type="number"
                                    min="1"
                                    max={selectedItem.quantity}
                                    value={moveQuantity}
                                    onChange={(e) => setMoveQuantity(e.target.value)}
                                    data-testid="move-quantity-input"
                                    className="mt-1"
                                />
                            </div>
                            
                            <div className="flex gap-3 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setIsMoveModalOpen(false)}
                                    className="flex-1"
                                >
                                    Anulează
                                </Button>
                                <Button
                                    onClick={handleMoveToMagazin}
                                    data-testid="confirm-move-button"
                                    className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                                >
                                    <ArrowRight size={18} className="mr-2" />
                                    Mută în Magazin
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Edit Modal */}
            <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Editează Articol
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedItem && (
                        <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
                            <div className="bg-zinc-50 rounded-lg p-3">
                                <p className="font-medium">{selectedItem.product?.name}</p>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="edit-qty">Cantitate</Label>
                                    <Input
                                        id="edit-qty"
                                        type="number"
                                        min="1"
                                        value={editFormData.quantity}
                                        onChange={(e) => setEditFormData({...editFormData, quantity: e.target.value})}
                                        className="mt-1"
                                        required
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="edit-location">Locație</Label>
                                    <Select
                                        value={editFormData.location}
                                        onValueChange={(value) => setEditFormData({ ...editFormData, location: value })}
                                    >
                                        <SelectTrigger className="mt-1">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {locations.map((loc) => (
                                                <SelectItem key={loc} value={loc}>
                                                    {loc}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            
                            <div className="flex gap-3 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setIsEditModalOpen(false)}
                                    className="flex-1"
                                >
                                    Anulează
                                </Button>
                                <Button
                                    type="submit"
                                    className="flex-1 bg-blue-500 hover:bg-blue-600"
                                >
                                    Salvează
                                </Button>
                            </div>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Depozit;
